import type { TaskKind, TaskStatus } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  ErrorState,
  PageHeader,
  Toolbar,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ClipboardList } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskList } from '@/features/tasks/TaskList';
import { sortTasks, taskKeys, tasksQueryOptions, updateTask } from '@/features/tasks/queries';
import { notificationKeys } from '@/features/notifications/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';

export const Route = createFileRoute('/_app/tasks')({ component: TasksPage });

const KINDS: TaskKind[] = [
  'DOCUMENT_MISSING',
  'PAYMENT_EXPECTED',
  'OPTION_EXPIRING',
  'HOUSEKEEPING',
  'MAINTENANCE',
];
const STATUSES: TaskStatus[] = ['OPEN', 'DONE', 'DISMISSED'];

/** Centre de tâches — Opérations › Tâches (SCR-015). */
function TasksPage() {
  const { t } = useTranslation(['tasks', 'nav', 'common', 'errors']);
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TaskStatus>('OPEN');
  const [kind, setKind] = useState<TaskKind | undefined>(undefined);
  const [pendingId, setPendingId] = useState<string | undefined>(undefined);

  const query = useInfiniteQuery(tasksQueryOptions({ status, kind }));

  const mutation = useMutation({
    mutationFn: (input: { id: string; status: TaskStatus }) =>
      updateTask(input.id, { status: input.status }),
    onSuccess: async (_task, input) => {
      setPendingId(undefined);
      await queryClient.invalidateQueries({ queryKey: taskKeys.all });
      await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      toast.success(t(input.status === 'DONE' ? 'tasks:toasts.done' : 'tasks:toasts.dismissed'));
    },
    onError: () => setPendingId(undefined),
  });

  const rows = sortTasks(query.data?.pages.flatMap((page) => page.data) ?? []);

  return (
    <Container>
      <PageHeader
        title={t('tasks:title')}
        subtitle={t('tasks:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('nav:items.operations') },
          { label: t('tasks:title') },
        ]}
        actions={
          <Badge tone="neutral" size="lg">
            {t('tasks:countLabel', { count: rows.length })}
          </Badge>
        }
      />

      <Card>
        <CardHeader
          title={t('tasks:title')}
          icon={<ClipboardList className="ico ico-16" aria-hidden="true" />}
          iconTone="amber"
        />
        <CardBody density="flush">
          <Toolbar>
            {STATUSES.map((code) => (
              <ChipFilter key={code} active={status === code} onClick={() => setStatus(code)}>
                {t(`tasks:status.${code}`)}
              </ChipFilter>
            ))}
            <span className="spacer" />
            <ChipFilter active={kind === undefined} onClick={() => setKind(undefined)}>
              {t('tasks:filters.allKinds')}
            </ChipFilter>
            {KINDS.map((code) => (
              <ChipFilter
                key={code}
                active={kind === code}
                onClick={() => setKind(kind === code ? undefined : code)}
              >
                {t(`tasks:kind.${code}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          {query.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : (
            <TaskList
              tasks={rows}
              loading={query.isPending}
              pendingId={pendingId}
              onUpdate={(task, next) => {
                setPendingId(task.id);
                mutation.mutate({ id: task.id, status: next });
              }}
            />
          )}
        </CardBody>
        {query.hasNextPage ? (
          <div style={{ padding: 12, textAlign: 'center' }}>
            <Button
              variant="secondary"
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {t('common:actions.loadMore')}
            </Button>
          </div>
        ) : null}
      </Card>
    </Container>
  );
}
